import { escolaridadeTemCurso } from '../../../models/candidato.opcoes';
import { INPUT_CLASS, LABEL_CLASS, RespostasEtapa2Estado } from '../candidatoFormExternoEtapa2Page.definitions';
import BancoCombobox from './BancoCombobox';
import CampoSimNao from './CampoSimNao';

const REGIMES_TRABALHO = ['Remoto', 'Híbrido', 'Presencial'];

interface EtapaTrabalhoProps {
  respostas: RespostasEtapa2Estado;
  onChange: <K extends keyof RespostasEtapa2Estado>(campo: K, valor: RespostasEtapa2Estado[K]) => void;
}

export default function EtapaTrabalho({ respostas, onChange }: EtapaTrabalhoProps) {
  return (
    <>
      <h2 className="mb-6 text-2xl font-bold text-gray-800">Trabalho e Dados Bancários</h2>
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLASS}>Qual tipo de contrato de trabalho? *</label>
          <select
            className={INPUT_CLASS}
            value={respostas.tipoContrato ?? ''}
            onChange={(e) => onChange('tipoContrato', e.target.value)}
          >
            <option value="">Selecione</option>
            <option>CLT</option>
            <option>PJ</option>
            <option>Sócio</option>
            <option>Estagiário</option>
          </select>
        </div>
        {respostas.tipoContrato === 'PJ' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Número do CNPJ *</label>
              <input
                type="text"
                className={INPUT_CLASS}
                value={respostas.cnpj ?? ''}
                onChange={(e) => onChange('cnpj', e.target.value)}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Razão social *</label>
              <input
                type="text"
                className={INPUT_CLASS}
                value={respostas.razaoSocial ?? ''}
                onChange={(e) => onChange('razaoSocial', e.target.value)}
              />
            </div>
          </div>
        )}
        {respostas.tipoContrato === 'Estagiário' && (
          <>
            {!escolaridadeTemCurso(respostas.escolaridade) && (
              <div>
                <label className={LABEL_CLASS}>Qual curso está fazendo? *</label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  value={respostas.escolaridadeCurso ?? ''}
                  onChange={(e) => onChange('escolaridadeCurso', e.target.value)}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLASS}>Qual período? *</label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  value={respostas.estagioPeriodo ?? ''}
                  onChange={(e) => onChange('estagioPeriodo', e.target.value)}
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>Nome da instituição *</label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  value={respostas.estagioInstituicao ?? ''}
                  onChange={(e) => onChange('estagioInstituicao', e.target.value)}
                />
              </div>
            </div>
          </>
        )}

        <CampoSimNao
          label="Possui conta no Banco Itaú?"
          valor={respostas.possuiContaItau}
          onChange={(valor) => onChange('possuiContaItau', valor)}
        />
        {/* Com ou sem Itaú, todos informam uma conta de outro banco (decisão do RH). */}
        <p className="text-sm text-gray-600">Informe os dados de uma conta em outro banco (que não seja o Itaú).</p>
        <div>
          <label className={LABEL_CLASS}>Banco *</label>
          <BancoCombobox codigo={respostas.outroBancoCodigo} onChange={(codigo) => onChange('outroBancoCodigo', codigo)} />
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <label className={LABEL_CLASS}>Agência *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.outroBancoAgencia ?? ''}
              onChange={(e) => onChange('outroBancoAgencia', e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Conta *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.outroBancoConta ?? ''}
              onChange={(e) => onChange('outroBancoConta', e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Dígito *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.outroBancoDigito ?? ''}
              onChange={(e) => onChange('outroBancoDigito', e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Pix *</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={respostas.outroBancoPix ?? ''}
              onChange={(e) => onChange('outroBancoPix', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className={LABEL_CLASS}>Qual será o seu regime de trabalho? *</label>
          <p className="mb-2 text-xs text-gray-500">Pode marcar mais de uma opção.</p>
          <div className="flex flex-wrap gap-4">
            {REGIMES_TRABALHO.map((opcao) => (
              <label key={opcao} className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={respostas.regimeTrabalho.includes(opcao)}
                  onChange={() =>
                    onChange(
                      'regimeTrabalho',
                      respostas.regimeTrabalho.includes(opcao)
                        ? respostas.regimeTrabalho.filter((r) => r !== opcao)
                        : [...respostas.regimeTrabalho, opcao]
                    )
                  }
                />
                {opcao}
              </label>
            ))}
          </div>
        </div>
        {respostas.regimeTrabalho.length > 0 && !respostas.regimeTrabalho.every((r) => r === 'Remoto') && (
          <div>
            <label className={LABEL_CLASS}>Informe as linhas de ônibus que serão utilizadas e o valor da tarifa *</label>
            <textarea
              className={INPUT_CLASS}
              rows={2}
              value={respostas.linhaOnibusTarifa ?? ''}
              onChange={(e) => onChange('linhaOnibusTarifa', e.target.value)}
            />
          </div>
        )}
      </div>
    </>
  );
}
